import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { AxiosError } from "axios";
import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import { ApiResources } from "@/api/resources";
import useTableItemDetails from "@/components/DetailsSection/hooks/useTableItemDetails";
import Dialog from "@/components/Dialog/Dialog";
import { COMPANIES_ACTIONS, GeneralDialogProps } from "@/shared";
import DialogActions from "@/shared/components/buttons/DialogActions";
import { refreshTableAndUpdateDetails } from "@/shared/functions/functions";
import useResponse from "@/shared/hooks/useResponse";
import { useCompaniesTableStore } from "@/stores/useTableStore";

import {
  CompanyNameObject,
  MOVE_COMPANY_ERROR_MESSAGES,
  MOVE_COMPANY_KEYS,
  MoveCompaniesPayload,
  MoveOptionsData
} from "../types";
import MoveCompanyConfirmDialog from "./MoveCompanyConfirmDialog";
import MoveCompanyDialogContent from "./MoveCompanyDialogContent";
import MoveCompanyErrorDialog from "./MoveCompanyErrorDialog";

const MoveCompanyDialog = ({ isOpen, onClose }: GeneralDialogProps) => {
  const { t } = useTranslation();
  const { getData, postData } = useAxiosPrivate();

  const { handleCommonError, handleResponse } = useResponse();
  const { getTableItemDetails } = useTableItemDetails();

  const { detailsData } = useCompaniesTableStore();

  const [isLoading, setIsLoading] = useState(false);
  const [isReadyToConfirm, setIsReadyToConfirm] = useState<boolean>(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] =
    useState<boolean>(false);

  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);

  const [optionsData, setOptionsData] = useState<MoveOptionsData | null>(null);

  const initialPayload = {
    [MOVE_COMPANY_KEYS.SourceCompany]: null,
    [MOVE_COMPANY_KEYS.TargetCompany]: null
  };

  const [payload, setPayload] = useState<MoveCompaniesPayload>(initialPayload);

  const resetAllValues = () => {
    setIsReadyToConfirm(false);
    setPayload(initialPayload);
    setOptionsData(null);
  };

  const handleCloseCreateCompanyDialog = () => {
    onClose(COMPANIES_ACTIONS.MoveCompany);
  };

  const { sourceCompany, targetCompany } = payload;

  const companyNameObject = {
    sourceCompany: sourceCompany?.name,
    targetCompany: targetCompany?.name
  } satisfies CompanyNameObject;

  const handleCreateChildSubmit = async () => {
    try {
      setIsConfirmDialogOpen(false);
      setIsLoading(true);

      const editableItemHasOpenedDetails =
        detailsData &&
        (detailsData.id === sourceCompany?.id ||
          detailsData.id === targetCompany?.id);

      const finalPayload = {
        sourceCompanyId: sourceCompany?.id,
        targetCompanyId: targetCompany?.id
      };

      await postData(ApiResources.CompaniesMove, finalPayload);

      const successMessage = t(
        "Alert##move company success",
        companyNameObject
      );

      refreshTableAndUpdateDetails({
        valueInSearchField: null,
        resourceId: detailsData?.id,
        successMessage,
        itemHasOpenedDetails: editableItemHasOpenedDetails,
        getTableItemDetails,
        handleResponse
      });

      handleCloseCreateCompanyDialog();
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      if (
        axiosError?.response?.data?.message ===
        MOVE_COMPANY_ERROR_MESSAGES.ExceedsLimit
      ) {
        setIsErrorDialogOpen(true);
      } else {
        handleCommonError(error, t);
      }
    }
    setIsLoading(false);
  };

  const getAllCompanies = async () => {
    try {
      const { data } = await getData(ApiResources.CompaniesMoveOptions);

      setOptionsData(data);
    } catch (error) {
      handleCommonError(error, t);
      setOptionsData(null);
    }
  };

  useEffect(() => {
    isOpen && getAllCompanies();
  }, [isOpen]);

  return (
    <>
      <Dialog
        data-testid="move-company-dialog"
        title={t(`Dialog##move company##title`)}
        description={t(`Dialog##move company##description`)}
        extendToMaxWidth
        isTitleSeparator
        isActionsSeparator
        open={isOpen}
        close={handleCloseCreateCompanyDialog}
        submit={() => setIsConfirmDialogOpen(true)}
        slotProps={{
          transition: {
            onExited: () => resetAllValues()
          }
        }}
        actions={
          <DialogActions
            onClose={handleCloseCreateCompanyDialog}
            isLoading={isLoading}
            isreadyToConfirm={isReadyToConfirm}
            confirmationButtonText={t("Button##move")}
            confirmationButtonTestId="move-company-button"
            cancelButtonText={t("Button##close")}
            cancelButtonTestId="close-move-company"
          />
        }
      >
        <MoveCompanyDialogContent
          optionsData={optionsData}
          payload={payload}
          setPayload={setPayload}
          setIsReadyToConfirm={setIsReadyToConfirm}
        />
      </Dialog>

      <MoveCompanyConfirmDialog
        isConfirmDialogOpen={isConfirmDialogOpen}
        companyNameObject={companyNameObject}
        isLoading={isLoading}
        setIsConfirmDialogOpen={setIsConfirmDialogOpen}
        handleCreateChildSubmit={handleCreateChildSubmit}
      />

      <MoveCompanyErrorDialog
        isOpen={isErrorDialogOpen}
        setIsErrorDialogOpen={setIsErrorDialogOpen}
      />
    </>
  );
};

export default MoveCompanyDialog;
